import type { DelegateEvent } from '@disy/lib/event-util';
import { on } from '@disy/lib/event-util';
import type { Teardown } from '@disy/lib/teardown';
import { throttle } from '@disy/lib/throttle';
import { uniqueId } from '@disy/lib/unique-id';

import { applyGlobalCursor } from 'cadenza/utils/global-cursor/global-cursor';
import 'cadenza/workbook/layout-management/resize-handle.css';
import type { ResizeInfo, WithResizeInfo } from 'cadenza/workbook/layout-management/resize-utils';
import { getResize } from 'cadenza/workbook/layout-management/resize-utils';
import type { MapViewSidebar } from 'cadenza/workbook/view/map/sidebar/map-view-sidebar';
import { View } from 'cadenza/workbook/view/view';
import { isLinearLayout } from 'cadenza/workbook/worksheet-layout';
import {
  getSidebarResizeDirection,
  handleSidebarResizing,
  isNearSidebarBorder,
  isResizeDirectionAllowed,
} from 'cadenza/workbook/worksheet/worksheet-grid/sidebar-resizing';
import { handleViewMoving } from 'cadenza/workbook/worksheet/worksheet-grid/view-moving';
import { handleViewResizing } from 'cadenza/workbook/worksheet/worksheet-grid/view-resizing';
import type { WorksheetGrid } from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-grid';
import {
  VIEW_MARGIN,
  getDraggableOrNonDraggableClass,
} from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';

export function setupWorksheetLayouting(
  relativeContainer: WorksheetGrid,
  layoutManagementAllowed: boolean,
  mapViewSidebarResizeAllowed: boolean,
) {
  const teardowns: Teardown[] = [];
  let currentView: WithResizeInfo<View> | null;
  let currentSidebar: WithResizeInfo<MapViewSidebar> | null;
  let isViewMoving: boolean;
  const globalCursorSourceId = uniqueId();
  const onPointerMove = (event: PointerEvent) => {
    if (currentView || currentSidebar) {
      return; // resizing or moving in progress
    }
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return; // pointer released outside the window, so there is no closest function
    }
    const closestView =
      layoutManagementAllowed || mapViewSidebarResizeAllowed ?
        (target.closest<WithResizeInfo<View>>('.d-worksheet-view') ?? undefined)
      : undefined;
    const closestSidebar =
      mapViewSidebarResizeAllowed ?
        target.closest<WithResizeInfo<MapViewSidebar>>('.d-map-view-sidebar') || isNearSidebarBorder(event, closestView)
      : undefined;

    // Sidebar is always nested within view, this means that order of if statements must check sidebar first
    if (closestSidebar && isResizeDirectionAllowed(event, closestSidebar)) {
      delete closestSidebar.dResize;
      closestSidebar.dResize = getSidebarResizeDirection(event, closestSidebar);
      applyGlobalCursor(getCursor(closestSidebar.dResize), globalCursorSourceId);
    } else if (closestSidebar && closestSidebar.dResize) {
      delete closestSidebar.dResize;
    } else if (!closestSidebar && closestView) {
      delete closestView.dResize;
      isViewMoving = false;

      if (
        !closestView.classList.contains('is-maximized') &&
        !closestView.classList.contains('is-integrated') &&
        !isLinearLayout()
      ) {
        if (target.closest('.' + getDraggableOrNonDraggableClass())) {
          isViewMoving = true;
        } else {
          closestView.dResize = getResize(event, closestView, VIEW_MARGIN);
        }
      }

      applyGlobalCursor(getCursor(closestView.dResize), globalCursorSourceId);
    } else {
      applyGlobalCursor('', globalCursorSourceId);
    }
  };
  teardowns.push(
    on(window, 'pointermove', throttle(onPointerMove, 50, { trailing: false })),
    on(
      relativeContainer,
      'pointerdown',
      (event: DelegateEvent<PointerEvent>) => {
        // Only continue for mouse events if it's left click, and event.target is an Element
        if ((event.pointerType === 'mouse' && event.button !== 0) || !(event.target instanceof Element)) {
          return;
        }

        let view: WithResizeInfo<View> | undefined;
        if (layoutManagementAllowed || mapViewSidebarResizeAllowed) {
          view = event.delegateTarget instanceof View ? (event.delegateTarget as WithResizeInfo<View>) : undefined;
        }
        const sidebar =
          mapViewSidebarResizeAllowed ?
            event.target.closest<WithResizeInfo<MapViewSidebar>>('.d-map-view-sidebar') ||
            isNearSidebarBorder(event, view)
          : undefined;
        const sidebarResize = sidebar && sidebar.dResize && Object.values(sidebar.dResize).some(Boolean);

        const isViewResizing = view && view.dResize && Object.values(view.dResize).some(Boolean);
        if ((currentView || (!isViewResizing && !isViewMoving)) && (!sidebar || !sidebarResize)) {
          return;
        }

        if (sidebar) {
          currentSidebar = sidebar;
          handleSidebarResizing(sidebar);
        } else if (view && !view.classList.contains('is-integrated')) {
          currentView = view;
          if (isViewMoving) {
            handleViewMoving(event, view, relativeContainer, globalCursorSourceId);
          } else {
            handleViewResizing(event, view, relativeContainer);
          }
          isViewMoving = false;
        }
      },
      { delegate: '.d-worksheet-view' },
    ),

    on(window, 'pointerup', (event: PointerEvent) => {
      currentView = null;
      currentSidebar = null;
      onPointerMove(event);
    }),
  );
  return teardowns;
}

function getCursor(
  { top, right, bottom, left }: ResizeInfo = { top: false, right: false, bottom: false, left: false },
) {
  if ((top && left) || (bottom && right)) {
    return 'nwse-resize';
  } else if ((top && right) || (bottom && left)) {
    return 'nesw-resize';
  } else if (top || bottom) {
    return 'ns-resize';
  } else if (left || right) {
    return 'ew-resize';
  }
  return '';
}
